import { useId } from "react";
import type { Period } from "@/api/books";
import { Button } from "@/components/ui/button";
import { TextField } from "@/components/ui/field";
import { todayIso } from "@/lib/dates";

/** 1 April of the financial year a date falls in. */
export function fyStartIso(iso: string): string {
  const year = Number(iso.slice(0, 4));
  const month = Number(iso.slice(5, 7));
  return `${month >= 4 ? year : year - 1}-04-01`;
}

/** The financial year to date. */
export function thisFy(now: string = todayIso()): Period {
  return { from: fyStartIso(now), to: now };
}

/** The whole of last month. */
export function lastMonth(now: string = todayIso()): Period {
  const first = new Date(`${now.slice(0, 7)}-01T00:00:00Z`);
  const end = new Date(first);
  end.setUTCDate(0);
  const start = new Date(end);
  start.setUTCDate(1);
  return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
}

type PeriodPickerProps = {
  value: Period;
  onChange: (period: Period) => void;
  /** Only an end date (a balance sheet is "as of"). */
  asOfOnly?: boolean;
};

export function PeriodPicker({ value, onChange, asOfOnly = false }: PeriodPickerProps) {
  const id = useId();
  return (
    <div className="flex flex-wrap items-end gap-3">
      {!asOfOnly && (
        <TextField
          id={`${id}-from`}
          label="From"
          type="date"
          value={value.from}
          onChange={(from) => onChange({ ...value, from })}
        />
      )}
      <TextField
        id={`${id}-to`}
        label={asOfOnly ? "As of" : "To"}
        type="date"
        value={value.to}
        onChange={(to) => onChange({ ...value, to })}
      />
      <div className="flex gap-2 pb-1">
        <Button variant="outline" size="sm" onClick={() => onChange(thisFy())}>
          This FY
        </Button>
        <Button variant="outline" size="sm" onClick={() => onChange(lastMonth())}>
          Last month
        </Button>
      </div>
    </div>
  );
}
